import { useState } from 'react'
import { describeDue, markHandled, setWaitingOn, type LetterTask } from '@core/letters'
import { Button, Card, ErrorNote } from '@design/components'
import { DocumentThumb } from '@features/documents/DocumentThumb'
import { LetterSummaryCard } from './LetterSummaryCard'
import { todayIso } from '@core/dates'

/**
 * One letter, and the only question it has to answer: do I have to do
 * something, and by when?
 *
 * The original is one tap away, because a summary is never enough for a letter
 * from a lawyer — you read the actual page before you act on it.
 */
export function LetterCard({
  task,
  userId,
  onView,
  onChanged,
}: {
  task: LetterTask
  userId: string
  onView: (documentId: string) => void
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [waitingText, setWaitingText] = useState('')

  const overdue = task.dueDate ? task.dueDate < todayIso() : false

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    try {
      await fn()
      onChanged()
    } catch {
      setError('Dat lukte niet. Probeer opnieuw.')
    }
    setBusy(false)
  }

  return (
    <Card className={`mb-3 ${overdue && !task.doneAt ? 'border-danger/40' : ''}`}>
      <div className="flex gap-3">
        {task.documentId && (
          <div className="w-16 shrink-0">
            <DocumentThumb documentId={task.documentId} onOpen={() => onView(task.documentId!)} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="font-medium truncate">{task.title}</p>
            <span className={`text-sm shrink-0 ${overdue ? 'text-danger' : 'text-muted'}`}>
              {describeDue(task.dueDate)}
            </span>
          </div>
          {!expanded && task.detail && (
            <p className="text-sm text-muted mt-1 line-clamp-2">{task.detail}</p>
          )}
          {task.waitingOn && (
            <p className="text-sm text-muted mt-1">Wachten op: {task.waitingOn}</p>
          )}
        </div>
      </div>

      {task.summary && (
        <div className="mt-3">
          {expanded ? (
            <LetterSummaryCard summary={task.summary} />
          ) : (
            <button
              onClick={() => setExpanded(true)}
              className="text-sm text-muted underline underline-offset-2"
            >
              Wat staat erin?
            </button>
          )}
        </div>
      )}

      <ErrorNote>{error}</ErrorNote>

      {!task.doneAt && (
        <div className="mt-3 space-y-2">
          {waiting ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void run(async () => {
                  await setWaitingOn(task.id, waitingText)
                  setWaiting(false)
                })
              }}
            >
              <input
                autoFocus
                value={waitingText}
                onChange={(e) => setWaitingText(e.target.value)}
                placeholder="Waarop wacht je?"
                className="w-full min-h-[3rem] px-4 rounded-xl bg-surface border border-line mb-2"
              />
              <Button type="submit" tone="secondary" disabled={busy || !waitingText.trim()}>
                Bewaren
              </Button>
            </form>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => void run(() => markHandled(task.id, userId))} disabled={busy}>
                Afgehandeld
              </Button>
              {!task.waitingOn && (
                <Button tone="quiet" onClick={() => setWaiting(true)} disabled={busy}>
                  Wachten op…
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
